import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import { IdentityClientFormBuilder } from '@modules/IdentityServer/services/IdentityClientFormBuildur';
import { IIdentityServerClientResponse, IGrantTypes } from '@modules/IdentityServer/identityServer.model';
import { NgbModalRef, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StringHelper } from '@core/utils/StringHelper';
import { IdentityClientService } from '@modules/IdentityServer/services/IdentityClientService';

@Component({
  selector: 'sf-app-identity-clients-form-urls',
  templateUrl: './identity-clients-form-urls.component.html',
  styleUrls: ['./identity-clients-form-urls.component.scss'],
})
export class IdentityClientsFormUrlsComponent implements OnInit {
  @Input() form: FormGroup;
  @Input() client: IIdentityServerClientResponse;
  public predefinedGrantTypes: IGrantTypes;
  public showSaveRedirectUris = false;
  public showSavePostRedirectUris = false;
  public showSaveCorsOrigins = false;
  public showSaveGrantTypes = false;
  public editId: number;
  public newRow = false;
  public items: FormArray;
  public key: string;
  public grantPredefindKey: any;
  public loading = false;
  public submitting = false;
  @Input() error: any;
  public modalReference: NgbModalRef;
  public deletedType: string;
  public selectedValue: string;
  public flag = false;
  public oldValue: any;
  public validationMsg = false;
  public duplicateNameValidation = false;

  constructor(
    private formBuilder: IdentityClientFormBuilder,
    private modalService: NgbModal,
    private service: IdentityClientService,
  ) { }

  ngOnInit() {
    this.getGrantTypes();
  }

  async getGrantTypes() {
    try {
      this.loading = true;
      const client = await this.service.getGrantTypes();
      this.predefinedGrantTypes = client.result;
    } catch (err) {
      this.error = err;
    } finally {
      this.loading = false;
    }
  }

  toggle(id: number, key: string) {
    if (this.newRow) {
      const length = this.form.controls[this.key].value.length;
      const control = <FormArray>this.form.controls[this.key];
      control.removeAt(length - 1);
      this.newRow = false;
      this.editId = null;
      this.flag = false;
    }
    this.removeItem(this.key);
    this.hideEdit(this.key);
    this.key = key;
    this.showEdit(key);
    const target = <FormArray>this.form.controls[this.key];
    this.oldValue = target.value[id];
    this.editId = id;
    this.flag = true;
    this.validationMsg = false;
    this.duplicateNameValidation = false;
  }

  resetRedirectUri(key?: string) {
    this.key = key || this.key;
    if (this.newRow) {
      const length = this.form.controls[this.key].value.length;
      const control = <FormArray>this.form.controls[this.key];
      control.removeAt(length - 1);
      this.flag = false;
      this.newRow = false;
    }
    this.removeItem(key);
    this.hideEdit(this.key);
    this.editId = null;
    this.validationMsg = false;
    this.duplicateNameValidation = false;
  }

  showEdit(key: string) {
    switch (key) {
      case 'redirectUris':
        this.showSaveRedirectUris = true;
        if (this.newRow) {
          this.items = this.form.controls[key] as FormArray;
          this.items.push(this.formBuilder.createRedirectUri(''));
        }
        break;
      case 'postLogoutRedirectUris':
        this.showSavePostRedirectUris = true;
        if (this.newRow) {
          this.items = this.form.controls[key] as FormArray;
          this.items.push(this.formBuilder.createPostRedirectUri(''));
        }
        break;
      case 'allowedCorsOrigins':
        this.showSaveCorsOrigins = true;
        if (this.newRow) {
          this.items = this.form.controls[key] as FormArray;
          this.items.push(this.formBuilder.createCorsOrigin(''));
        }
        break;
      case 'allowedGrantTypes':
        this.showSaveGrantTypes = true;
        if (this.newRow) {
          this.items = this.form.controls[key] as FormArray;
          this.items.push(this.formBuilder.createGrantType(''));
        }
        break;
    }
  }

  hideEdit(key: string) {
    switch (key) {
      case 'redirectUris':
        this.showSaveRedirectUris = false;
        break;
      case 'postLogoutRedirectUris':
        this.showSavePostRedirectUris = false;
        break;
      case 'allowedCorsOrigins':
        this.showSaveCorsOrigins = false;
        break;
      case 'allowedGrantTypes':
        this.showSaveGrantTypes = false;
        break;
    }
  }

  addItem(key: string): void {
    if (this.key !== undefined && this.newRow) {
      const length = this.form.controls[this.key].value.length;
      const control = <FormArray>this.form.controls[this.key];
      control.removeAt(length - 1);
      this.flag = false;
      this.newRow = false;
    }
    this.removeItem(this.key);
    this.hideEdit(this.key);
    this.key = key;
    this.newRow = true;
    this.showEdit(key);
    this.editId = this.items.length - 1;
    this.flag = true;
    this.validationMsg = false;
    this.duplicateNameValidation = false;
  }

  duplicateIdentifier(field: string) {
    const control = <FormArray>this.form.controls[this.key];
    const target = control.controls[this.editId];
    let flag = 0;
    for (let i = 0; i < control.value.length; i++) {
      if (control.value[i][field] === target.value[field] && control.value[i].deleteOperation !== 0) {
        flag++;
      }
    }
    if (flag > 1) {
      this.duplicateNameValidation = true;
      return true;
    }
    this.duplicateNameValidation = false;
    return false;
  }

  update() {
    const control = <FormArray>this.form.controls[this.key];
    const target = control.controls[this.editId];
    if (target.invalid || this.duplicateNameValidation) {
      this.validationMsg = true;
      return;
    }
    this.hideEdit(this.key);
    this.newRow = false;
    this.editId = null;
    this.flag = false;
    this.validationMsg = false;
    this.duplicateNameValidation = false;
  }

  deleteRedirectUri() {
    const control = <FormArray>this.form.controls[this.key];
    const target = control.controls[this.editId];
    if (target.value['id'] === 0) {
      control.removeAt(this.editId);
    } else {
      target.patchValue({ deleteOperation: 0 });
      this.items = this.form.controls[this.key] as FormArray;
      this.form.controls[this.deletedType].value.push(this.items.value[this.editId]['id']);
    }
    this.editId = null;
    this.modalReference.close();
  }

  async setPredefined() {
    this.items = this.form.controls['allowedGrantTypes'] as FormArray;
    for (let i = 0; i < this.items.length; i++) {
      const item = this.items.value[i]['id'];
      if (item) {
        this.form.controls['deleteAllowedGrantTypesIds'].value.push(this.items.value[i]['id']);
      } else {
        this.items.controls.splice(i, this.items.length);
        break;
      }
      const target = this.items.controls[i];
      target.patchValue({ deleteOperation: 0 });
    }
    const allowedGrantTypes = StringHelper.toArray(this.selectedValue);
    for (let i = 0; i < allowedGrantTypes.length; i++) {
      this.items.push(this.formBuilder.createGrantType({ grantType: allowedGrantTypes[i] }));
    }
    this.modalReference.close();
  }

  confirmModal(content: any, index: number, key: string, deletedType: string) {
    this.hideEdit(this.key);
    this.removeItem(this.key);
    this.flag = false;
    this.newRow = false;
    this.editId = index;
    this.key = key;
    this.deletedType = deletedType;
    this.modalReference = this.modalService.open(content, { centered: true });
  }

  editGrantTypesModal(content: any, select: any) {
    this.selectedValue = select.value;
    this.modalReference = this.modalService.open(content, { centered: true });
  }

  removeItem(key?: string) {
    if (this.newRow) {
      const length = this.form.controls[this.key].value.length;
      const control = <FormArray>this.form.controls[this.key];
      control.removeAt(length - 1);
    } else if (this.flag) {
      this.key = key || this.key;
      const control = <FormArray>this.form.controls[this.key];
      const target = control.controls[this.editId];
      target.patchValue(this.oldValue);
      this.flag = false;
      this.hideEdit(this.key);
      this.editId = null;
    }
  }

}
